import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CardItem from '@/components/CardItem';
import { getCards } from '@/lib/api';
import { useCompare, useFavorites } from '@/lib/hooks';
import { toast } from 'sonner';

export default function FavoritesPage() {
  const [cards, setCards] = useState([]);
  const [allCards, setAllCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const { favoriteIds, toggleFavorite, isFavorite, clearFavorites } = useFavorites();
  const { toggleCompare, isInCompare } = useCompare();

  useEffect(() => {
    setLoading(true);
    getCards({}).then(r => setAllCards(r.data)).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setCards(allCards.filter(c => favoriteIds.includes(c.id))); }, [favoriteIds, allCards]);

  if (favoriteIds.length === 0) return (
    <div className="max-w-4xl mx-auto px-6 py-20 text-center" data-testid="favorites-empty">
      <Heart className="w-12 h-12 text-[#E4E4E7] mx-auto mb-4" />
      <h2 className="font-heading font-bold text-2xl text-[#09090B] mb-2">No Favorites Yet</h2>
      <p className="text-[#71717A] mb-6">Save cards you like for quick access.</p>
      <Link to="/"><Button className="bg-[#002FA7] hover:bg-[#002175] text-white rounded-full h-10 px-6">Browse Cards</Button></Link>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="favorites-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#09090B]">Saved Cards</h1>
          <p className="text-[13px] text-[#71717A]">{cards.length} saved card{cards.length !== 1 ? 's' : ''}</p>
        </div>
        <Button variant="outline" size="sm" data-testid="clear-favorites-btn"
          onClick={() => { clearFavorites(); toast.info('Cleared'); }}
          className="text-[12px] rounded-full text-red-500 hover:text-red-600 gap-1.5">
          <Trash2 className="w-3.5 h-3.5" /> Clear All
        </Button>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="border border-[#E4E4E7] rounded-xl overflow-hidden animate-pulse">
              <div className="h-28 bg-[#E4E4E7]" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-[#E4E4E7] rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {cards.map((card, i) => (
            <div key={card.id} className={`animate-fade-in-up stagger-${(i % 6) + 1}`}>
              <CardItem
                card={card}
                onToggleFavorite={(id) => { toggleFavorite(id); toast.info('Removed from favorites'); }}
                onToggleCompare={(id) => { toggleCompare(id); toast.success(isInCompare(id) ? 'Removed' : 'Added to compare'); }}
                isFavorite={true}
                isCompare={isInCompare(card.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
